/**
 * MGT-001 staff identity (alpha): every staff member has a name and one trait derived from their persisted record id,
 * so saves need no new fields. Each trait changes exactly one documented variable.
 */
import { CONFIG } from './config.js';
import type { EconomyData } from './types.js';
import { tier } from './airports.js';

export type Trait = 'Quick Hands' | 'Strong' | 'Cheerful' | 'Tidy' | 'Steady';
export const TRAITS: readonly Trait[] = ['Quick Hands', 'Strong', 'Cheerful', 'Tidy', 'Steady'];
export const TRAIT_EFFECTS: Record<Trait, string> = {
    'Quick Hands': 'serves at a desk 10% faster',
    Strong: 'carries 6 bags instead of 4',
    Cheerful: 'their desk line loses patience 10% slower',
    Tidy: 'carries 6 pieces of litter instead of 4',
    Steady: 'costs 10% less in wages',
};
const NAMES = ['Agnes', 'Dot', 'Priya', 'Gus', 'Dana', 'Marco', 'Bea', 'Hollis', 'Penrose', 'Ivy', 'Theo', 'June', 'Omar', 'Lena', 'Ray', 'Mina', 'Sol', 'Kit', 'Ada', 'Ned'];
const hash = (id: string) => { let h = 0x811c9dc5; for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 0x01000193) >>> 0; return h; };
export function staffTrait(recordId: string): Trait { return TRAITS[hash(recordId) % TRAITS.length]; }
export function staffName(recordId: string): string { return NAMES[(hash(recordId) >>> 8) % NAMES.length]; }

/** The role a player sees: desk helpers are named by where they work. */
export function roleLabel(staff: { role: string; homeStage: number }): string {
    if (staff.role === 'porter') return 'Baggage handler';
    if (staff.role === 'cleaner') return 'Cleaner';
    if (staff.role === 'runner') return 'Cash runner';
    if (staff.role === 'pilot') return 'Pilot';
    const home = staff.homeStage;
    if (home === 2) return 'Security officer';
    if (home === 4 || home === 5) return 'Retail host';
    if (home === 7) return 'Dispatcher';
    return home >= 0 ? `Agent · ${CONFIG.stages[home].name}` : 'Roaming agent';
}

/** Specialists hired directly at the admin desk; the cash runner only exists once Act I is over. */
export type Specialist = 'porter' | 'cleaner' | 'runner';
export const SPECIALIST_BASE: Record<Specialist, number> = { porter: 600, cleaner: 500, runner: 800 };
export function specialistCost(role: Specialist, e: Pick<EconomyData, 'run'>, count: number): number {
    return Math.ceil(SPECIALIST_BASE[role] * tier(e).costMult * (1 + count * 0.5));
}
/** Bags or litter a staff member carries at once. */
export function staffCarry(recordId: string, kind: 'bags' | 'litter'): number {
    return staffTrait(recordId) === (kind === 'bags' ? 'Strong' : 'Tidy') ? 6 : 4;
}
